import {
  Card,
  CardActions,
  CardContent,
  Collapse,
  Stack,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Icon from '@components/Icon/Icon';

const messages = defineMessages({
  more: {
    id: 'GLOBAL.more',
    defaultMessage: 'More',
  },
  less: {
    id: 'GLOBAL.less',
    defaultMessage: 'Less',
  },
});

interface CollapsibleCardProps {
  /**
   * Content of the visible card
   */
  cardContent: React.ReactNode;
  /**
   * Hidden content that is visible when the card is expanded
   */
  expandedContent: React.ReactNode;
  /**
   * Customized action content
   */
  actionContent?: React.ReactNode;
  /**
   * Callback when the visible card is clicked
   */
  onCardClick?: () => void;
  /**
   * Callback when the user clicks the expand button
   */
  onExpand?: () => void;
  /**
   * Is the card expanded?
   */
  isExpanded?: boolean;
}

export default function CollapsibleCard({
  cardContent,
  expandedContent,
  actionContent,
  onCardClick,
  onExpand,
  isExpanded = false,
}: CollapsibleCardProps) {
  const { formatMessage } = useIntl();
  const [expanded, setExpanded] = useState(isExpanded);

  const handleExpand = () => {
    onExpand?.();
  };
  const handleCollapse = () => {};

  const handleExpandClick = async () => {
    const newExpanded = !expanded;
    setExpanded(newExpanded);
    if (newExpanded) {
      handleExpand();
    } else {
      handleCollapse();
    }
  };

  const handleCardClick = () => onCardClick?.();

  return (
    <Card sx={{ width: '100%' }}>
      <CardContent
        sx={{
          py: 1,
          cursor: onCardClick ? 'pointer' : 'auto',
        }}
        onClick={handleCardClick}
      >
        {cardContent}
      </CardContent>
      <Collapse in={expanded} unmountOnExit>
        <CardContent>{expandedContent}</CardContent>
      </Collapse>
      <CardActions
        onClick={handleExpandClick}
        sx={{
          backgroundColor: 'info.light',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
      >
        {actionContent ?? (
          <Stack alignItems="center" direction="row" gap={1}>
            <Icon
              clickable
              color="primary.main"
              name="chevron-down"
              rotation={expanded ? 180 : 0}
              style={{ width: '100%' }}
              type="fas"
            />
            <Typography color="primary.main" variant="subtitle2">
              {formatMessage(expanded ? messages.less : messages.more)}
            </Typography>
          </Stack>
        )}
      </CardActions>
    </Card>
  );
}
